/**
 * Formatacao e mascaras brasileiras.
 *
 * Dinheiro trafega em centavos (inteiro) do formulario ate o banco e ate a
 * API de pagamentos em C#. A conversao para float acontece somente na
 * exibicao — nunca em calculo de cobranca.
 */

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const compactCurrencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('pt-BR');

export function formatCents(cents: number | null | undefined, options?: { compact?: boolean }): string {
  if (cents === null || cents === undefined) return '—';
  const value = cents / 100;
  return options?.compact ? compactCurrencyFormatter.format(value) : currencyFormatter.format(value);
}

export function formatArea(value: number | null | undefined, unit = 'm²'): string {
  if (value === null || value === undefined) return '—';
  return `${numberFormatter.format(value)} ${unit}`;
}

export function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

export function relativeDays(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 30) return `ha ${days} dias`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'ha 1 mes' : `ha ${months} meses`;
}

export const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/** Aplica um molde onde `#` e digito: '###.###.###-##'. */
function applyMask(value: string, mask: string): string {
  const digits = digitsOnly(value);
  let result = '';
  let index = 0;
  for (const char of mask) {
    if (index >= digits.length) break;
    if (char === '#') {
      result += digits[index];
      index += 1;
    } else {
      result += char;
    }
  }
  return result;
}

export const maskCpf = (value: string) => applyMask(value, '###.###.###-##');
export const maskCnpj = (value: string) => applyMask(value, '##.###.###/####-##');
export const maskCep = (value: string) => applyMask(value, '#####-###');

/** Celular tem 11 digitos, fixo tem 10. A mascara segue o que foi digitado. */
export function maskPhone(value: string): string {
  const digits = digitsOnly(value).slice(0, 11);
  return applyMask(digits, digits.length > 10 ? '(##) #####-####' : '(##) ####-####');
}

export function maskDocument(value: string): string {
  return digitsOnly(value).length > 11 ? maskCnpj(value) : maskCpf(value);
}

/** Digitacao progressiva de dinheiro: "12345" -> "R$ 123,45". */
export function maskCurrencyFromDigits(value: string): string {
  const digits = digitsOnly(value).slice(0, 15);
  if (!digits) return '';
  return currencyFormatter.format(Number(digits) / 100);
}

/** Le o valor mascarado de volta para centavos. */
export function parseCentsFromMasked(value: string): number | null {
  const digits = digitsOnly(value);
  return digits ? Number(digits) : null;
}

/** Numero para WhatsApp: 55 + DDD + numero, sem simbolos. */
export function toWhatsappNumber(value: string): string {
  const digits = digitsOnly(value);
  return digits.startsWith('55') ? digits : `55${digits}`;
}

export function buildWhatsappLink(phone: string, message: string): string {
  return `https://wa.me/${toWhatsappNumber(phone)}?text=${encodeURIComponent(message)}`;
}
